import type { OfferView } from '@xchange/shared';

/**
 * Simple mode trades at the best price on the book and never leaves an
 * order waiting: a buy takes only what is on offer at that price, a sell
 * only what is bid for. More than that is for Advanced, where people name
 * their own price. Decided 16 October 2026.
 */
export interface SimpleBook {
  /** The best price by someone else on the other side, in the pair's stored unit. */
  readonly price: bigint;
  /** All of it at that price, in the base coin's smallest units. */
  readonly available: bigint;
}

/** A level of the book: an order on the trading screen, or a public level with no owner. */
type BookOffer = Pick<OfferView, 'price' | 'remaining'> & { mine?: OfferView['mine'] };

/**
 * What Simple can trade against: the other side's best price by someone
 * else, and everything at that price. `opposite` is the other side of the
 * book, best first (lowest offer for a buy, highest bid for a sell). Null
 * when nobody else is on that side.
 */
export function simpleBook(opposite: readonly BookOffer[]): SimpleBook | null {
  const others = opposite.filter((o) => o.mine !== 'maker' && BigInt(o.remaining) > 0n);
  const best = others[0];
  if (best === undefined) return null;
  const price = BigInt(best.price);
  const available = others
    .filter((o) => BigInt(o.price) === price)
    .reduce((t, o) => t + BigInt(o.remaining), 0n);
  return { price, available };
}

/** The amount Simple places: what was asked for, or what is there if that is less. */
export function simpleAmount(
  want: bigint,
  book: SimpleBook | null,
): { amount: bigint; capped: boolean } {
  if (book === null) return { amount: 0n, capped: want > 0n };
  return want > book.available
    ? { amount: book.available, capped: true }
    : { amount: want, capped: false };
}
